import NavLinkAdapter from '@fuse/core/NavLinkAdapter';
import { styled, useTheme } from '@mui/material/styles';
import Grow from '@mui/material/Grow';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import clsx from 'clsx';
import { memo, useMemo, useState } from 'react';
import {
	useFloating,
	FloatingPortal,
	offset,
	flip,
	shift,
	autoUpdate,
	useHover,
	useInteractions,
	useDismiss,
	type Placement,
} from '@floating-ui/react';
import { ListItemButton } from '@mui/material';
import type { ListItemButtonProps } from '@mui/material';
import type { Location } from 'history';
import isUrlInChildren from '@fuse/core/FuseNavigation/isUrlInChildren';
import usePathname from '@fuse/hooks/usePathname';
import FuseNavBadge from '../../FuseNavBadge';
import FuseNavItem from '../../FuseNavItem';
import type { FuseNavItemComponentProps } from '../../FuseNavItem';
import FuseSvgIcon from '../../../FuseSvgIcon';

const Root = styled(ListItemButton)<ListItemButtonProps>(({ theme }) => ({
	color: theme.palette.text.primary,
	minHeight: 48,
	cursor: 'pointer',
	'&.active, &.active:hover, &.active:focus': {
		backgroundColor: `${theme.palette.secondary.main}!important`,
		color: `${theme.palette.secondary.contrastText}!important`,
		'&.open': {
			backgroundColor: 'rgba(0,0,0,.08)',
		},
		'& > .fuse-list-item-text': {
			padding: '0 0 0 16px',
		},
		'& .fuse-list-item-icon': {
			color: 'inherit',
		},
	},
}));

type FuseNavHorizontalCollapseProps = FuseNavItemComponentProps & {
	location: Location;
};

/**
 * FuseNavHorizontalCollapse component helps rendering Horizontal Fuse Navigation Item with children
 * Used in FuseNavVerticalItems and FuseNavHorizontalItems
 */
function FuseNavHorizontalCollapse(props: FuseNavHorizontalCollapseProps) {
	const { item, nestedLevel, dense, checkPermission } = props;
	const [opened, setOpened] = useState(false);
	const pathname = usePathname();
	const theme = useTheme();
	const component = item.url ? NavLinkAdapter : 'li';

	const itemProps = useMemo(
		() => ({
			...(component !== 'li' && {
				disabled: item.disabled,
				to: item.url,
				end: item.end,
				role: 'button',
				exact: item?.exact,
			}),
		}),
		[item, component],
	);

	const popperPlacement: Placement = theme.direction === 'ltr' ? 'right' : 'left';

	const { refs, floatingStyles, context } = useFloating({
		open: opened,
		onOpenChange: setOpened,
		placement: popperPlacement,
		middleware: [offset(0), flip(), shift()],
		whileElementsMounted: autoUpdate,
	});

	const hover = useHover(context, {
		delay: { open: 0, close: 150 },
	});

	const dismiss = useDismiss(context);

	const { getReferenceProps, getFloatingProps } = useInteractions([hover, dismiss]);

	const memoizedContent = useMemo(
		() => (
			<ul className="relative px-0">
				<div
					ref={refs.setReference}
					{...getReferenceProps()}
				>
					<Root
						component={component}
						className={clsx('fuse-list-item', opened && 'open', isUrlInChildren(item, pathname) && 'active')}
						aria-owns={opened ? 'menu-fuse-list-grow' : undefined}
						aria-haspopup="true"
						sx={item.sx}
						{...itemProps}
					>
						{item.icon && typeof item.icon === 'string' && (
							<FuseSvgIcon
								color="action"
								className={clsx('fuse-list-item-icon shrink-0', item.iconClass)}
							>
								{item.icon}
							</FuseSvgIcon>
						)}

						<ListItemText
							className="fuse-list-item-text"
							primary={item.title}
							classes={{ primary: 'text-md truncate' }}
						/>

						{item.badge && (
							<FuseNavBadge
								className="mx-1"
								badge={item.badge}
							/>
						)}
						<IconButton
							disableRipple
							className="h-3 w-3 p-0 ltr:ml-1 rtl:mr-1"
							color="inherit"
						>
							<FuseSvgIcon
								size={12}
								className="arrow-icon"
							>
								{theme.direction === 'ltr' ? 'heroicons-outline:chevron-right' : 'heroicons-outline:chevron-left'}
							</FuseSvgIcon>
						</IconButton>
					</Root>
				</div>
				<FloatingPortal>
					{opened && (
						<div
							ref={refs.setFloating}
							style={{
								...floatingStyles,
								zIndex: 999 + (nestedLevel || 0) + 1,
							}}
							className={clsx('z-999', !opened && 'pointer-events-none')}
							{...getFloatingProps()}
						>
							<Grow
								in={opened}
								id="menu-fuse-list-grow"
								style={{ transformOrigin: '0 0 0' }}
							>
								<Paper className="rounded-sm">
									{item.children && (
										<ul className={clsx('popper-navigation-list', dense && 'dense', 'px-0')}>
											{item.children.map((_item) => (
												<FuseNavItem
													key={_item.id}
													type={`horizontal-${_item.type}`}
													item={_item}
													nestedLevel={(nestedLevel || 0) + 1}
													dense={dense}
												/>
											))}
										</ul>
									)}
								</Paper>
							</Grow>
						</div>
					)}
				</FloatingPortal>
			</ul>
		),
		[
			refs,
			getReferenceProps,
			component,
			opened,
			item,
			pathname,
			itemProps,
			dense,
			nestedLevel,
			floatingStyles,
			getFloatingProps,
		],
	);

	if (checkPermission && !item?.hasPermission) {
		return null;
	}

	return memoizedContent;
}

const NavHorizontalCollapseWithMemo = memo(FuseNavHorizontalCollapse);

export default NavHorizontalCollapseWithMemo;
